// Canonical plan-item rules: slot roles and plan variants, booked /
// cancelled state, and the transforms that move an item between those
// states. Spec: docs/todo/567-plan-roles.md (TODO #567).
//
// "Booked" lives only on Booking rows (Jake, 2026-09-25): about 30
// readers — map pins, budget, reservation `secured`, Split, visits — take
// "a Booking row exists" to mean booked, so a slot never stores it.
// bookSlot() turns a slot into a minimal Booking in one step instead.
//
// Consumers (this file is the ONE source of the vocabulary and rules):
//   • Worker   — src/lib/mcp.js (slot / booking tools), src/lib/day-plan-core.js
//   • Web      — @core/plan-core: lib/trip-state.ts, components/BookingEditor.tsx,
//                the budget / pins / reservations readers
//   • Mobile   — mobile/lib/plan-core.js, a BYTE-IDENTICAL twin maintained
//                ONLY by `bash scripts/sync_core.sh plan-core` (Metro can't
//                import outside mobile/). Never hand-edit the mobile copy.
// Semantics are pinned by scripts/smoke_plan_core.mjs.
//
// Transforms are pure and work on the bare collections (itinerary map,
// bookings array, reservations array) like trip-state-core: they return
// the SAME reference when nothing changed, and are id-guarded so mobile's
// offline drain can replay an op without duplicating rows.

// ── Vocabulary ────────────────────────────────────────────────────────

/** Slot role; absent = planned. Alternates are plan variants, not a role. */
export const SLOT_ROLES = ['option'];
export const BANDS = ['morning', 'afternoon', 'evening'];
export const LANES = ['do', 'eat'];
/**
 * Slot type for a part of a day marked deliberately open ("Open — wander",
 * #597). It holds a band and a lane and nothing else: it is not a plan
 * item, so the day rows, the agenda and the planned counts skip it
 * (isOpenSlot) and only the Day Grid draws it. applySlotFields drops the
 * fields that would make it one (time, place link, toBook, decideBy, role,
 * must, phone).
 */
export const OPEN_SLOT_TYPE = 'open';
// Fields an open slot never carries (applySlotFields drops them).
const OPEN_SLOT_DROPS = ['time', 'refId', 'refKind', 'mealPlan', 'mealPlanMeal', 'mealPlanKey', 'role', 'must', 'toBook', 'decideBy', 'phone'];
/**
 * Booking status; absent = confirmed. 'pending' = requested, waiting for
 * the venue to confirm: it still stands (isActiveBooking) but is not
 * secured (reservation-core). 'cancelled' stays on the day, struck through.
 */
export const BOOKING_STATUSES = ['cancelled', 'pending'];

const DECIDE_BY_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/;
const PLAN_ID_RE = /^[a-z0-9][a-z0-9-]{0,23}$/;
const HHMM_RE = /^(\d{1,2}):(\d{2})/;

export class PlanFieldError extends Error {
  constructor(field, message) {
    super(`${field}: ${message}`);
    this.name = 'PlanFieldError';
    this.field = field;
  }
}

// A phone number as typed: digits with an optional leading '+', spaces,
// dots, dashes and parentheses, and an optional extension ("ext 12", "x12",
// "#12", "内線12"). At least 3 digits, up to PHONE_MAX characters. Full-width
// digits and the Unicode dashes (U+2010–U+2015, U+2212, U+FF0D) read as
// their ASCII forms.
const EXT = '(?:ext\\.?|x|#|内線)';
const PHONE_RE = new RegExp(`^\\+?[\\d\\s().-]+(?:\\s*${EXT}\\s*\\d+)?$`, 'i');
const EXT_TAIL_RE = new RegExp(`\\s*${EXT}\\s*\\d+$`, 'i');
const ISO_DATE_ONLY_RE = /^\d{4}-\d{2}-\d{2}$/;
export const PHONE_MAX = 40;

// ASCII digits and dashes, "(0)" after a country code dropped
// ("+81 (0)75-561-0015" dials +81 75 561 0015).
function asciiPhone(v) {
  return v
    .normalize('NFKC')
    .replace(/[‐-―−－]/g, '-')
    .trim()
    .replace(/^(\+\d{1,3})\s*\(0\)\s*/, '$1 ');
}

/** A phone number a booking or slot may hold. */
export function isValidPhone(v) {
  if (typeof v !== 'string') return false;
  const t = asciiPhone(v);
  return (
    v.trim().length <= PHONE_MAX &&
    !ISO_DATE_ONLY_RE.test(t) &&
    PHONE_RE.test(t) &&
    (t.match(/\d/g) || []).length >= 3
  );
}

/**
 * The `tel:` href for a phone number: spaces, dots, dashes and
 * parentheses stripped, a leading '+' kept, a "(0)" after the country
 * code and an extension dropped (a tel: link can't wait for it). null
 * when there are fewer than 3 digits or the text is a bare ISO date.
 */
export function telHref(phone) {
  if (typeof phone !== 'string') return null;
  const a = asciiPhone(phone);
  if (ISO_DATE_ONLY_RE.test(a)) return null;
  const t = a.replace(EXT_TAIL_RE, '');
  const digits = t.replace(/\D/g, '');
  if (digits.length < 3) return null;
  return `tel:${t.startsWith('+') ? '+' : ''}${digits}`;
}

/** 'YYYY-MM-DDTHH:MM', a trip-local wall time. */
export function isValidDecideBy(v) {
  return typeof v === 'string' && DECIDE_BY_RE.test(v);
}

/**
 * An item's `plan` (#586): absent = shared, shown whichever plan the day
 * follows; a variant id = only in that variant; BASE_PLAN = only in the
 * base plan, hidden while the day follows a variant.
 */
export const BASE_PLAN = 'base';

/** Plan variant id: lowercase, digits, '-', up to 24 chars ('b', 'rain'). */
export function isValidPlanId(v) {
  return typeof v === 'string' && PLAN_ID_RE.test(v) && v !== BASE_PLAN;
}

/** A value an item's `plan` may hold: BASE_PLAN or a variant id. */
export function isValidItemPlan(v) {
  return v === BASE_PLAN || isValidPlanId(v);
}

// undefined → leave the key alone; null / '' / false → delete it.
function isClear(v) {
  return v === null || v === '' || v === false;
}

function setOrClear(out, key, v, validate, message) {
  if (v === undefined) return;
  if (isClear(v)) {
    delete out[key];
    return;
  }
  if (validate && !validate(v)) throw new PlanFieldError(key, message);
  out[key] = v;
}

// Reads that tolerate a hand-edited blob: plans / slots that aren't
// arrays, and null or id-less entries, are skipped rather than thrown on
// (a throw here takes down the day views and get_day for the whole trip).
function planList(day) {
  const plans = day && Array.isArray(day.plans) ? day.plans : [];
  return plans.filter((p) => p && typeof p.id === 'string');
}

function slotList(day) {
  const slots = day && Array.isArray(day.slots) ? day.slots : [];
  return slots.filter((s) => s && typeof s === 'object');
}

// ── Field rules ───────────────────────────────────────────────────────

/**
 * Apply the plan fields (and `phone`) in `input` to a copy of `slot`.
 * undefined leaves a field alone; null, '' or false clears it. Throws PlanFieldError on an
 * invalid value. Other slot fields pass through untouched.
 */
export function applySlotFields(slot, input) {
  const out = { ...slot };
  const i = input || {};
  setOrClear(out, 'role', i.role, (v) => SLOT_ROLES.includes(v), `one of ${SLOT_ROLES.join(', ')}`);
  if (i.must !== undefined) {
    if (i.must === true) out.must = true;
    else if (isClear(i.must)) delete out.must;
    else throw new PlanFieldError('must', 'true or false');
  }
  if (i.toBook !== undefined) {
    if (i.toBook === true) out.toBook = true;
    else if (isClear(i.toBook)) delete out.toBook;
    else throw new PlanFieldError('toBook', 'true or false');
  }
  setOrClear(out, 'plan', i.plan, isValidItemPlan, `'${BASE_PLAN}' or a plan id like 'b' or 'rain'`);
  setOrClear(out, 'band', i.band, (v) => BANDS.includes(v), `one of ${BANDS.join(', ')}`);
  setOrClear(out, 'lane', i.lane, (v) => LANES.includes(v), `one of ${LANES.join(', ')}`);
  setOrClear(out, 'decideBy', i.decideBy, isValidDecideBy, "'YYYY-MM-DDTHH:MM'");
  setOrClear(out, 'phone', typeof i.phone === 'string' ? i.phone.trim() : i.phone, isValidPhone, 'a phone number like +81 3 1234 5678');
  // notIn: the plans a shared item sits out ('base' and/or variant ids), so
  // it can be in base and C but not B. An empty list clears it.
  if (i.notIn !== undefined) {
    if (isClear(i.notIn) || (Array.isArray(i.notIn) && i.notIn.length === 0)) delete out.notIn;
    else if (Array.isArray(i.notIn) && i.notIn.every(isValidItemPlan)) out.notIn = [...new Set(i.notIn)];
    else throw new PlanFieldError('notIn', `a list of '${BASE_PLAN}' or plan ids`);
  }
  // An open part of the day holds a label, band and lane (and its plan
  // membership); anything that would make it a plan item is dropped, so it
  // never reaches To book, deadlines, the tracker or the map.
  if (out.type === OPEN_SLOT_TYPE) {
    // A time it carried becomes its part of the day, so the marker stays put.
    if (!out.band && out.time) {
      const band = bandForTime(out.time);
      if (band) out.band = band;
    }
    for (const k of OPEN_SLOT_DROPS) delete out[k];
  }
  return out;
}

/**
 * Apply status / refunded / plan / decideBy to a copy of `booking`.
 * Setting status 'cancelled' stamps cancelledAt (from `now`, an ISO
 * string) unless already set; setting 'pending' or clearing status also
 * clears cancelledAt and refunded. refunded only sticks on a cancelled
 * booking.
 */
export function applyBookingPlanFields(booking, input, now) {
  const out = { ...booking };
  const i = input || {};
  if (i.status !== undefined) {
    if (isClear(i.status)) {
      delete out.status;
      delete out.cancelledAt;
      delete out.refunded;
    } else if (BOOKING_STATUSES.includes(i.status)) {
      out.status = i.status;
      if (i.status === 'cancelled') {
        if (!out.cancelledAt) out.cancelledAt = now || new Date().toISOString();
      } else {
        delete out.cancelledAt;
        delete out.refunded;
      }
    } else {
      throw new PlanFieldError('status', `one of ${BOOKING_STATUSES.join(', ')}`);
    }
  }
  if (i.refunded !== undefined) {
    if (i.refunded === true) {
      if (out.status !== 'cancelled') throw new PlanFieldError('refunded', 'only a cancelled booking can be refunded');
      out.refunded = true;
    } else if (isClear(i.refunded)) delete out.refunded;
    else throw new PlanFieldError('refunded', 'true or false');
  }
  setOrClear(out, 'plan', i.plan, isValidItemPlan, `'${BASE_PLAN}' or a plan id like 'b' or 'rain'`);
  setOrClear(out, 'decideBy', i.decideBy, isValidDecideBy, "'YYYY-MM-DDTHH:MM'");
  return out;
}

/**
 * Apply title / plans / activePlan to a copy of an itinerary day. `plans`
 * replaces the variant list ([{ id, when }]); an activePlan must name one
 * of the day's variants.
 */
export function applyDayFields(day, input) {
  const out = { slots: [], ...day };
  const i = input || {};
  if (i.title !== undefined) {
    if (isClear(i.title)) delete out.title;
    else if (typeof i.title === 'string' && i.title.trim().length <= 80) out.title = i.title.trim();
    else throw new PlanFieldError('title', 'a string up to 80 chars');
  }
  if (i.plans !== undefined) {
    if (isClear(i.plans) || (Array.isArray(i.plans) && i.plans.length === 0)) {
      delete out.plans;
    } else if (Array.isArray(i.plans)) {
      const seen = new Set();
      out.plans = i.plans.map((p, idx) => {
        if (!p || !isValidPlanId(p.id)) throw new PlanFieldError(`plans[${idx}].id`, `a plan id like 'b' or 'rain' (not '${BASE_PLAN}')`);
        if (seen.has(p.id)) throw new PlanFieldError(`plans[${idx}].id`, 'duplicate');
        seen.add(p.id);
        const when = typeof p.when === 'string' ? p.when.trim() : '';
        return when ? { id: p.id, when } : { id: p.id };
      });
    } else {
      throw new PlanFieldError('plans', 'an array of { id, when }');
    }
  }
  if (i.activePlan !== undefined) {
    if (isClear(i.activePlan)) delete out.activePlan;
    else if (!isValidPlanId(i.activePlan)) throw new PlanFieldError('activePlan', 'a plan id');
    else out.activePlan = i.activePlan;
  }
  if (out.activePlan && !planList(out).some((p) => p.id === out.activePlan)) {
    throw new PlanFieldError('activePlan', `no plan '${out.activePlan}' on this day`);
  }
  return out;
}

/**
 * Delete a plan variant from a day. Its slots either join the base plan
 * (`dropSlots` false — the default) or are removed. If the day was
 * following that variant it goes back to the base plan. Once the last
 * variant goes, BASE_PLAN tags mean nothing and are cleared, so every item
 * is plain again. Same day object back when the variant doesn't exist.
 */
export function removeDayPlan(day, planId, opts) {
  if (!day || !planList(day).some((p) => p.id === planId)) return day;
  const drop = !!(opts && opts.dropSlots);
  const out = { ...day };
  const plans = planList(day).filter((p) => p.id !== planId);
  if (plans.length) out.plans = plans;
  else delete out.plans;
  if (out.activePlan === planId) delete out.activePlan;
  const left = plans.map((p) => p.id);
  out.slots = slotList(day).flatMap((s) => {
    if (s.plan === planId) {
      if (drop) return [];
      return [pruneSlotNotIn(rejoinBase(s, plans.length > 0), left)];
    }
    // A shared slot that was in this plan and no other (notIn named the
    // rest) goes the way a slot tagged for it does: deleted with dropSlots,
    // else it joins the base plan, i.e. sits out every remaining variant;
    // with none left it is plain.
    if (!s.plan && Array.isArray(s.notIn) && slotOnlyInPlan(s, day, planId)) {
      if (drop) return [];
      const { notIn: _n, ...plain } = s;
      return [left.length ? { ...plain, notIn: left } : plain];
    }
    if (s.plan === BASE_PLAN && !plans.length) return [pruneSlotNotIn(rejoinBase(s, false), left)];
    return [pruneSlotNotIn(s, left)];
  });
  return out;
}

/** The plans an item is in on this day: BASE_PLAN and/or variant ids. */
export function itemInPlans(item, day) {
  return [BASE_PLAN, ...planList(day).map((p) => p.id)].filter((id) => inPlan(item, day, id === BASE_PLAN ? null : id));
}

/** Is the item in plan `planId` and no other plan of this day? */
export function slotOnlyInPlan(item, day, planId) {
  const inPlans = itemInPlans(item, day);
  return inPlans.length === 1 && inPlans[0] === planId;
}

/**
 * A slot's notIn list once the day's variants are `planIds`: ids the day no
 * longer has drop out ('base' stays only while variants remain), and the
 * list is deleted when nothing is left or it would sit the slot out of every
 * plan. Same slot object back when nothing changes.
 */
export function pruneSlotNotIn(slot, planIds) {
  if (!slot || !Array.isArray(slot.notIn)) return slot;
  const ids = Array.isArray(planIds) ? planIds : [];
  const rest = ids.length ? slot.notIn.filter((id) => id === BASE_PLAN || ids.includes(id)) : [];
  const covers = [BASE_PLAN, ...ids].every((id) => rest.includes(id));
  if (rest.length && !covers && rest.length === slot.notIn.length) return slot;
  const { notIn: _n, ...clean } = slot;
  return rest.length && !covers ? { ...clean, notIn: rest } : clean;
}

// An item leaving a deleted variant: base-only while other variants
// remain (so it doesn't turn up inside them), else plain.
function rejoinBase(item, otherVariants) {
  const { plan: _p, ...rest } = item;
  return otherVariants ? { ...rest, plan: BASE_PLAN } : rest;
}

/**
 * removeDayPlan on the whole state: also clears `plan` on the day's
 * bookings booked for that variant (booking plan ids are per day, keyed
 * by dateStart), so they return to the base plan instead of vanishing
 * from the day views. Bookings are never removed. Same state back when
 * nothing changes.
 */
export function removeDayPlanInState(state, dateISO, planId, opts) {
  const itinerary = state.itinerary || {};
  const day = itinerary[dateISO];
  const nextDay = day ? removeDayPlan(day, planId, opts) : day;
  const bookings = state.bookings || [];
  let touched = false;
  const variantsLeft = nextDay ? planList(nextDay).length > 0 : false;
  const nextBookings = bookings.map((b) => {
    if (!b || b.dateStart !== dateISO) return b;
    if (b.plan === planId || (b.plan === BASE_PLAN && !variantsLeft)) {
      touched = true;
      return rejoinBase(b, b.plan === planId && variantsLeft);
    }
    return b;
  });
  if (nextDay === day && !touched) return state;
  return {
    ...state,
    itinerary: nextDay === day ? itinerary : { ...itinerary, [dateISO]: nextDay },
    bookings: touched ? nextBookings : bookings,
  };
}

/** Next unused variant id for a day: 'b', 'c', … */
export function nextPlanId(day) {
  const used = new Set(planList(day).map((p) => p.id));
  for (const c of 'bcdefghijklmnopqrstuvwxyz') if (!used.has(c)) return c;
  return `p${used.size + 1}`;
}

// ── Derived state ─────────────────────────────────────────────────────

// 'canceled' is the spelling some older rows carry; read both, write
// 'cancelled'.
function isCancelled(b) {
  return b.status === 'cancelled' || b.status === 'canceled';
}

/** A booking that still stands (not cancelled). A pending one does. */
export function isActiveBooking(b) {
  return !!b && !isCancelled(b);
}

/** A booking requested and waiting for the venue to confirm. */
export function isPendingBooking(b) {
  return !!b && b.status === 'pending';
}

/** Budget: a cancelled booking counts until it is marked refunded. */
export function countsTowardBudget(b) {
  return !!b && !(isCancelled(b) && b.refunded === true);
}

/**
 * Give back what a booking secured: 'secured' reservations for `refId` go
 * to 'tracking' unless a confirmed booking (active, not pending) in
 * `bookings` still covers the place. `bookings` is the list after the
 * change: without the removed booking (unbookSlot), or with the booking
 * that just turned pending. Same array when nothing changes.
 */
export function releaseReservations(reservations, bookings, refId) {
  const arr = reservations || [];
  if (!refId || (bookings || []).some((b) => b && b.refId === refId && isActiveBooking(b) && !isPendingBooking(b))) {
    return arr;
  }
  let touched = false;
  const mapped = arr.map((r) => {
    if (r.refId === refId && r.status === 'secured') {
      touched = true;
      return { ...r, status: 'tracking' };
    }
    return r;
  });
  return touched ? mapped : arr;
}

/** Band for an 'HH:MM' time: before 12 morning, before 17 afternoon. */
export function bandForTime(time) {
  const m = typeof time === 'string' ? HHMM_RE.exec(time) : null;
  if (!m) return null;
  const h = Number(m[1]);
  if (h < 12) return 'morning';
  if (h < 17) return 'afternoon';
  return 'evening';
}

/** Is this slot a deliberately open part of the day, not a plan item? */
export function isOpenSlot(slot) {
  return !!slot && slot.type === OPEN_SLOT_TYPE;
}

export function slotBand(slot) {
  if (!slot) return null;
  return slot.band || bandForTime(slot.time);
}

const EAT_TYPES = new Set(['meal', 'restaurant', 'restaurants', 'bar', 'bars']);

export function slotLane(slot) {
  if (!slot) return null;
  if (slot.lane) return slot.lane;
  return EAT_TYPES.has(slot.type) || EAT_TYPES.has(slot.refKind) ? 'eat' : 'do';
}

/** The day's plan variants (tolerant: skips malformed entries). */
export function dayPlans(day) {
  return planList(day);
}

// The plan an item sits in: null (shared), BASE_PLAN, or a variant id. A
// plan id the day doesn't have (deleted by another writer, hand-edited)
// reads as shared, so the item stays on screen instead of vanishing into
// a variant nobody can switch to.
function planOf(item, day) {
  const id = (item && item.plan) || null;
  if (id === BASE_PLAN) return BASE_PLAN;
  return id && planList(day).some((p) => p.id === id) ? id : null;
}

// The plans an item sits out, as declared on this day ('base' included).
function notInOf(item, day) {
  const list = item && Array.isArray(item.notIn) ? item.notIn : [];
  return list.filter((id) => id === BASE_PLAN || planList(day).some((p) => p.id === id));
}

// Is an item in plan `planId` (null = the base plan) on this day?
function inPlan(item, day, planId) {
  const p = planOf(item, day);
  const ok = !p ? true : p === BASE_PLAN ? !planId : p === planId;
  return ok && !notInOf(item, day).includes(planId || BASE_PLAN);
}

/** The plans an item sits out on this day ('base' and/or variant ids). */
export function itemNotIn(item, day) {
  return notInOf(item, day);
}

/** The variant a day follows; null = the base plan (also for a dangling id). */
export function activePlanId(day) {
  const id = (day && day.activePlan) || null;
  return id && planList(day).some((p) => p.id === id) ? id : null;
}

/** Is this slot in the plan the day is following? Shared slots always are. */
export function isSlotActive(slot, day) {
  return inPlan(slot, day, activePlanId(day));
}

/** The plan an item belongs to on this day: null (shared), BASE_PLAN or a variant id. */
export function itemPlan(item, day) {
  return planOf(item, day);
}

/** The day's slots in the plan it is following. */
export function activeDaySlots(day) {
  return slotList(day).filter((s) => isSlotActive(s, day));
}

/**
 * Every plan the day is NOT following, for the collapsed "Plan B — if the
 * garden is closed" rows. The base plan appears here (id null) when the
 * day follows a variant. `slots` are the items only that plan adds;
 * `hides` are the items on screen now that switching to it would hide
 * (shared items stay in every plan).
 */
export function dayVariants(day) {
  // Open-cell markers (#597) are no plan's item, so no variant lists them.
  const slots = slotList(day).filter((s) => !isOpenSlot(s));
  const active = activePlanId(day);
  const entry = (id, when) => ({
    id,
    when,
    slots: slots.filter((s) => planOf(s, day) === (id || BASE_PLAN)),
    hides: slots.filter((s) => inPlan(s, day, active) && !inPlan(s, day, id)),
  });
  const out = [];
  if (active) out.push(entry(null, null));
  for (const p of planList(day)) {
    if (p.id === active) continue;
    out.push(entry(p.id, p.when || null));
  }
  return out;
}

/** An active booking in the plan the day is following (shared ones always are). */
export function isBookingActiveOnDay(b, day) {
  if (!isActiveBooking(b)) return false;
  return inPlan(b, day, activePlanId(day));
}

// ── Kind mapping ──────────────────────────────────────────────────────

const CATALOG_TYPE = {
  hotel: 'hotels', hotels: 'hotels',
  meal: 'restaurants', restaurant: 'restaurants', restaurants: 'restaurants',
  bar: 'bars', bars: 'bars',
  attraction: 'attractions', attractions: 'attractions',
  experience: 'experiences', experiences: 'experiences',
  shopping: 'shopping',
  destination: 'destinations', destinations: 'destinations',
};

/**
 * The catalog type a slot links to: its refKind when set, else derived
 * from its type (meal → restaurants). null for untyped links ('activity'
 * is ambiguous; freetext / flight are never catalog-backed).
 */
export function refKindForSlot(slot) {
  if (!slot || !slot.refId) return null;
  return CATALOG_TYPE[slot.refKind] || CATALOG_TYPE[slot.type] || null;
}

const KIND_FOR = {
  restaurants: 'restaurant', bars: 'restaurant',
  experiences: 'experience', attractions: 'experience',
  shopping: 'other', destinations: 'other',
};

// Slot types that can't become a one-step booking.
const BLOCKED = {
  freetext: 'freetext',
  open: 'freetext',
  hotel: 'hotel', hotels: 'hotel',
  flight: 'transit', rail: 'transit', rental: 'transit',
};

/**
 * Why a slot can't be booked in one step, or null when it can:
 * 'freetext' (a note, not a place), 'hotel' (needs a checkout date — use
 * the full editor), 'transit' (flight / rail / rental — full editor).
 */
export function bookSlotBlockReason(slot) {
  if (!slot) return 'missing';
  return BLOCKED[slot.type] || BLOCKED[slot.refKind] || null;
}

/** Booking kind for a slot: restaurant / experience / other, or null when blocked. */
export function slotTypeToBookingKind(slot) {
  if (bookSlotBlockReason(slot)) return null;
  const fromRef = KIND_FOR[CATALOG_TYPE[slot.refKind]];
  if (fromRef) return fromRef;
  switch (slot.type) {
    case 'meal': case 'restaurant': case 'bar': return 'restaurant';
    case 'attraction': case 'experience': return 'experience';
    default: return 'other';
  }
}

// ── Transforms ────────────────────────────────────────────────────────

const TRACKING = new Set([undefined, null, 'tracking', 'planned', 'opened']);

function findSlot(itinerary, dateISO, slotId) {
  const day = itinerary && itinerary[dateISO];
  if (!day || !Array.isArray(day.slots)) return null;
  return day.slots.find((s) => s.slotId === slotId) || null;
}

function withoutSlot(itinerary, dateISO, slotId) {
  const day = itinerary && itinerary[dateISO];
  if (!day || !Array.isArray(day.slots)) return itinerary;
  const next = day.slots.filter((s) => s.slotId !== slotId);
  if (next.length === day.slots.length) return itinerary;
  return { ...itinerary, [dateISO]: { ...day, slots: next } };
}

/**
 * Mark booked: remove the slot and add a Booking made from it, in one step.
 *
 *   state: { itinerary, bookings, reservations }
 *   args:  { dateISO, slotId, bookingId, now, time?, confirmation?, kind?, status? }
 *
 * The booking keeps the slot's label, time, place link (refId + refKind),
 * notes, phone, plan and decideBy, plus a `fromSlot` snapshot for undo. A
 * tracking reservation for the same place (targetDate equal to the day
 * or unset) becomes 'secured', unless `status` is 'pending' (a request
 * the venue hasn't confirmed secures nothing). Replay-safe: if `bookingId` already exists
 * the slot is only removed. Returns `state` itself when the slot is gone
 * and nothing else changes, or when the slot can't be booked
 * (see bookSlotBlockReason).
 */
export function bookSlot(state, args) {
  const itinerary = state.itinerary || {};
  const bookings = state.bookings || [];
  const reservations = state.reservations || [];
  const { dateISO, slotId, bookingId } = args;
  if (bookings.some((b) => b.id === bookingId)) {
    const nextItin = withoutSlot(itinerary, dateISO, slotId);
    return nextItin === itinerary ? state : { ...state, itinerary: nextItin };
  }
  const slot = findSlot(itinerary, dateISO, slotId);
  if (!slot) return state;
  const kind = args.kind || slotTypeToBookingKind(slot);
  if (!kind) return state;
  const booking = {
    id: bookingId,
    kind,
    label: slot.label || slot.refId || slot.type,
    dateStart: dateISO,
    dateEnd: null,
    time: args.time !== undefined ? args.time : slot.time || null,
    refId: slot.refId || null,
    refKind: refKindForSlot(slot),
    notes: slot.notes || null,
    confirmation: args.confirmation || null,
    createdAt: args.now,
    fromSlot: { ...slot, dateISO },
  };
  if (slot.plan) booking.plan = slot.plan;
  if (slot.decideBy) booking.decideBy = slot.decideBy;
  if (slot.phone) booking.phone = slot.phone;
  if (args.status === 'pending') booking.status = 'pending';
  let nextRes = reservations;
  if (booking.refId && booking.status !== 'pending') {
    let touched = false;
    const mapped = reservations.map((r) => {
      if (
        r.refId === booking.refId &&
        TRACKING.has(r.status) &&
        (!r.targetDate || r.targetDate === dateISO)
      ) {
        touched = true;
        return { ...r, status: 'secured' };
      }
      return r;
    });
    if (touched) nextRes = mapped;
  }
  return {
    ...state,
    itinerary: withoutSlot(itinerary, dateISO, slotId),
    bookings: [...bookings, booking],
    reservations: nextRes,
  };
}

const SLOT_TYPE_FOR_KIND = { restaurant: 'meal', experience: 'experience', other: 'activity' };

/**
 * Unbook: the reverse of bookSlot. Returns `state` unchanged when the
 * booking is gone (replay) or the slot's id is already on the target
 * day. Remove the booking and put the slot
 * back — from its `fromSlot` snapshot when it has one (original slotId,
 * type and plan fields), else built from the booking. `patch` (slot
 * fields, patchSlot rules) goes on top, e.g. an edit made in the same
 * form. The slot lands on `dateISO`, else the snapshot's day, else the
 * booking's dateStart. A 'secured' reservation for the place goes back
 * to 'tracking' unless another confirmed booking still covers it (a
 * pending one doesn't).
 *
 *   state: { itinerary, bookings, reservations }
 *   args:  { bookingId, slotId (used when there's no snapshot), dateISO?, patch? }
 *
 * Replay-safe: when the booking is already gone, returns `state`.
 */
export function unbookSlot(state, args) {
  const itinerary = state.itinerary || {};
  const bookings = state.bookings || [];
  const reservations = state.reservations || [];
  const booking = bookings.find((b) => b.id === args.bookingId);
  if (!booking) return state;
  const snap = booking.fromSlot || null;
  const day = args.dateISO || (snap && snap.dateISO) || booking.dateStart;
  if (!day) return state;
  let slot;
  if (snap) {
    const { dateISO: _d, ...rest } = snap;
    slot = rest;
  } else {
    slot = {
      slotId: args.slotId,
      type: SLOT_TYPE_FOR_KIND[booking.kind] || 'activity',
      ...(booking.label ? { label: booking.label } : {}),
      ...(booking.time ? { time: booking.time } : {}),
      ...(booking.notes ? { notes: booking.notes } : {}),
      ...(booking.phone ? { phone: booking.phone } : {}),
      ...(booking.refId ? { refId: booking.refId } : {}),
      ...(booking.refKind ? { refKind: booking.refKind } : {}),
      ...(booking.plan ? { plan: booking.plan } : {}),
      ...(booking.decideBy ? { decideBy: booking.decideBy } : {}),
    };
  }
  if (!slot.slotId) return state;
  if (args.patch) slot = patchOne(slot, args.patch);
  const target = itinerary[day] || { slots: [] };
  const targetSlots = Array.isArray(target.slots) ? target.slots : [];
  // A slot with that id is already on the day: do nothing rather than
  // drop the booking without putting anything back (its label, notes,
  // place link and cost would be lost).
  if (targetSlots.some((s) => s.slotId === slot.slotId)) return state;
  const nextItin = { ...itinerary, [day]: { ...target, slots: [...targetSlots, slot] } };
  const nextBookings = bookings.filter((b) => b.id !== booking.id);
  const nextRes = releaseReservations(reservations, nextBookings, booking.refId);
  return { ...state, itinerary: nextItin, bookings: nextBookings, reservations: nextRes };
}

/**
 * Move a slot to another day, keeping its slotId and every field except
 * a `plan` the new day doesn't declare (dropped — it joins the base
 * plan) and the `notIn` ids the new day doesn't have (pruneSlotNotIn). (A
 * remove + re-add minted a new id and dropped unlisted fields). Optional
 * `patch` goes through patchSlot rules. Replay-safe: when the slot is
 * already on `toDate` and gone from `fromDate`, returns the same map.
 */
export function moveSlot(itinerary, fromDate, toDate, slotId, patch) {
  const itin = itinerary || {};
  if (fromDate === toDate) return patch ? patchSlot(itin, fromDate, slotId, patch) : itin;
  const slot = findSlot(itin, fromDate, slotId);
  if (!slot) return itin;
  let moved = patch ? patchOne(slot, patch) : slot;
  const target = itin[toDate] || { slots: [] };
  // Plan variants are per day: a plan id the new day doesn't have would
  // hide the slot from every day view, so it becomes shared. BASE_PLAN
  // survives only onto a day that has variants.
  const targetPlans = planList(target);
  const keeps =
    moved.plan === BASE_PLAN ? targetPlans.length > 0 : targetPlans.some((p) => p.id === moved.plan);
  if (moved.plan && !keeps) {
    const { plan: _p, ...rest } = moved;
    moved = rest;
  }
  // notIn names the old day's plans: keep the ones this day has.
  moved = pruneSlotNotIn(moved, targetPlans.map((p) => p.id));
  const targetSlots = Array.isArray(target.slots) ? target.slots : [];
  const afterRemove = withoutSlot(itin, fromDate, slotId);
  if (targetSlots.some((s) => s.slotId === slotId)) return afterRemove;
  return { ...afterRemove, [toDate]: { ...target, slots: [...targetSlots, moved] } };
}

// Core slot fields a patch may set or clear, beyond the plan fields.
const SLOT_CORE_FIELDS = ['label', 'time', 'notes', 'type', 'refId', 'refKind'];

function patchOne(slot, patch) {
  const out = { ...slot };
  for (const k of SLOT_CORE_FIELDS) {
    if (patch[k] === undefined) continue;
    if (patch[k] === null || patch[k] === '') delete out[k];
    else out[k] = patch[k];
  }
  return applySlotFields(out, patch);
}

/**
 * Patch one slot: label / time / notes / type / refId / refKind plus the
 * plan fields (applySlotFields rules). undefined leaves a field alone;
 * null or '' clears it. Returns the same map when the slot isn't found.
 */
export function patchSlot(itinerary, dateISO, slotId, patch) {
  const itin = itinerary || {};
  const day = itin[dateISO];
  if (!day || !Array.isArray(day.slots)) return itin;
  let touched = false;
  const slots = day.slots.map((s) => {
    if (s.slotId !== slotId) return s;
    touched = true;
    return patchOne(s, patch || {});
  });
  return touched ? { ...itin, [dateISO]: { ...day, slots } } : itin;
}

/** Cancel a booking (optionally refunded). Same array when not found. */
export function cancelBooking(bookings, id, opts) {
  const arr = bookings || [];
  const o = opts || {};
  let touched = false;
  const next = arr.map((b) => {
    if (b.id !== id) return b;
    touched = true;
    return applyBookingPlanFields(
      b,
      { status: 'cancelled', refunded: o.refunded === true ? true : undefined },
      o.now,
    );
  });
  return touched ? next : arr;
}
